import { Api } from './types'
import { SERVER_API_URL } from '../configs/config'
import { retrieveRawInitData } from "@telegram-apps/sdk";
import { globalErrorHandler } from "../utils/error/globalErrorHandler";

class ApiService {
  private static instance: Api<unknown>

  private constructor() {}

  static getData(ignoreCache = false) {
    try {
      if (!ignoreCache) {
        // For local development
        const sparkDevTma = localStorage.getItem('sparkDevTma')
        if (sparkDevTma) {
          return sparkDevTma
        }
      }

      const initDataRaw = retrieveRawInitData();
      return initDataRaw ? `tma ${initDataRaw}` : null
    } catch (error) {
      return null
    }
  }

  private static createApiService() {
    const apiClient = new Api({ baseURL: SERVER_API_URL });

    apiClient.instance.interceptors.request.use(async (config) => {
      const data = ApiService.getData();

      config.headers.Authorization = data;
      return config;
    });

    // Retry config
    const MAX_RETRIES = 5;
    const MAX_BACKOFF = 3; // seconds, can be set higher if needed

    apiClient.instance.interceptors.response.use(
      (response) => response,
      async (error) => {
        const config = error.config;
        if (!config) return Promise.reject(error);

        // Track retry count on the config
        config.__retryCount = config.__retryCount || 0;

        // Only retry on network errors or 5xx, not 4xx
        const status = error?.response?.status;
        const isNetworkError = !error.response;
        const isRetryable = isNetworkError || (status >= 500 && status < 600);

        if (isRetryable && config.__retryCount < MAX_RETRIES) {
          // Calculate backoff: min(2^attempt, MAX_BACKOFF) + random(0-1000ms)
          const attempt = config.__retryCount;
          const jitter = Math.floor(Math.random() * 1000);
          const waitSeconds = Math.min(Math.pow(2, attempt), MAX_BACKOFF);
          config.__retryCount += 1;
          await new Promise((res) =>
            setTimeout(res, waitSeconds * 1000 + jitter)
          );
          return apiClient.instance.request(config);
        }

        // Handle errors using the global error handler
        globalErrorHandler.handleHttpError(error);
        return Promise.reject(error);
      }
    );

    return apiClient;
  }

  public static getInstance(): Api<unknown> {
    if (!ApiService.instance) {
      ApiService.instance = ApiService.createApiService()
    }

    return ApiService.instance
  }
}

export default ApiService
